// src/services/realtime.ts
import { RealtimeChannel } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { useTasks } from '../stores/useTasks'
import { useSettings } from '../stores/useSettings'
import { useSquads } from '../stores/useSquads'

// ── Channel registry ──────────────────────────────────────────────────────────
// Track active channels so we can cleanly remove them before re-subscribing.
// Without this, calling init() multiple times (e.g. on auth change) would
// pile up duplicate listeners.
const activeChannels: RealtimeChannel[] = []

function track(channel: RealtimeChannel): RealtimeChannel {
  activeChannels.push(channel)
  return channel
}

// ── Public API ────────────────────────────────────────────────────────────────

export const realtimeManager = {
  /**
   * Subscribe to all real-time channels needed by the app.
   * Safe to call multiple times — always cleans up previous channels first.
   */
  init: () => {
    realtimeManager.cleanup()

    // ── Tasks ──────────────────────────────────────────────────────────────
    // The PWA tasks store doesn't have granular merge helpers, so we reload
    // on any change. This is consistent with how the PWA store was designed.
    track(
      supabase
        .channel('tasks-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, () => {
          useTasks.getState().load()
        })
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('[realtime] tasks channel error — will retry on next init()')
          }
        })
    )

    // ── User settings ──────────────────────────────────────────────────────
    // IMPORTANT: use update() here only receives remote changes; the store's
    // update() writes back to DB which would retrigger — so we patch state
    // directly without a DB write by using the internal set via load().
    // We call load() rather than patching directly since the PWA settings
    // store doesn't expose a _patch helper.
    track(
      supabase
        .channel('settings-changes')
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'user_settings' }, () => {
          useSettings.getState().load()
        })
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('[realtime] settings channel error')
          }
        })
    )

    // ── Squads ─────────────────────────────────────────────────────────────
    track(
      supabase
        .channel('squad-membership-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'squad_members' }, () => {
          useSquads.getState().load()
        })
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('[realtime] squad_members channel error')
          }
        })
    )

    track(
      supabase
        .channel('squad-task-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'squad_tasks' }, (payload) => {
          const { eventType, new: n, old: o } = payload
          const store = useSquads.getState()
          if (eventType === 'INSERT') {
            const task = n as any
            const current = store.squadTasks
            if (task.squad_id === store.activeSquadId && !current.some(t => t.id === task.id)) {
              store._patchTasks([task, ...current])
            }
          } else if (eventType === 'UPDATE') {
            store._patchTasks(
              store.squadTasks.map(t => (t.id === (n as any).id ? { ...t, ...(n as any) } : t))
            )
          } else if (eventType === 'DELETE') {
            store._patchTasks(store.squadTasks.filter(t => t.id !== (o as any).id))
          }
        })
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('[realtime] squad_tasks channel error')
          }
        })
    )

    // ── Squad SNR leaderboard ──────────────────────────────────────────────
    track(
      supabase
        .channel('squad-snr-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'squad_snr' }, () => {
          useSquads.getState().load()
        })
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('[realtime] squad_snr channel error')
          }
        })
    )

    // ── Squad presence (Activity tab) ─────────────────────────────────────
    track(
      supabase
        .channel('squad-presence-changes')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'squad_presence' }, (payload) => {
          const { eventType, new: n } = payload
          if (eventType === 'INSERT' || eventType === 'UPDATE') {
            useSquads.getState()._patchPresence(n as any)
          }
        })
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('[realtime] squad_presence channel error')
          }
        })
    )
  },

  /**
   * Remove all active Supabase channels.
   * Call this on sign-out or before re-initialising.
   */
  cleanup: () => {
    activeChannels.forEach(ch => supabase.removeChannel(ch))
    activeChannels.length = 0
  },
}
